// Molar mass from a chemical formula. Handles nested groups "Ca(OH)2",
// brackets "K4[Fe(CN)6]", and hydrates "CuSO4·5H2O" / "CuSO4.5H2O".
//
// Atomic weights: IUPAC 2021 conventional values for the common elements, PubChem's
// periodic table for the rest (PubChem abridges a few, e.g. S 32.07, so those are
// overridden with the IUPAC figures).

export const ATOMS: Record<string, [number, string]> = {
  H: [1.008, 'Hydrogen'],
  He: [4.0026, 'Helium'],
  Li: [6.94, 'Lithium'],
  Be: [9.0122, 'Beryllium'],
  B: [10.81, 'Boron'],
  C: [12.011, 'Carbon'],
  N: [14.007, 'Nitrogen'],
  O: [15.999, 'Oxygen'],
  F: [18.998, 'Fluorine'],
  Ne: [20.18, 'Neon'],
  Na: [22.99, 'Sodium'],
  Mg: [24.305, 'Magnesium'],
  Al: [26.982, 'Aluminum'],
  Si: [28.085, 'Silicon'],
  P: [30.974, 'Phosphorus'],
  S: [32.06, 'Sulfur'],
  Cl: [35.45, 'Chlorine'],
  Ar: [39.95, 'Argon'],
  K: [39.098, 'Potassium'],
  Ca: [40.078, 'Calcium'],
  Sc: [44.956, 'Scandium'],
  Ti: [47.867, 'Titanium'],
  V: [50.942, 'Vanadium'],
  Cr: [51.996, 'Chromium'],
  Mn: [54.938, 'Manganese'],
  Fe: [55.845, 'Iron'],
  Co: [58.933, 'Cobalt'],
  Ni: [58.693, 'Nickel'],
  Cu: [63.546, 'Copper'],
  Zn: [65.38, 'Zinc'],
  Ga: [69.723, 'Gallium'],
  Ge: [72.63, 'Germanium'],
  As: [74.922, 'Arsenic'],
  Se: [78.971, 'Selenium'],
  Br: [79.904, 'Bromine'],
  Kr: [83.798, 'Krypton'],
  Rb: [85.468, 'Rubidium'],
  Sr: [87.62, 'Strontium'],
  Y: [88.906, 'Yttrium'],
  Zr: [91.224, 'Zirconium'],
  Nb: [92.906, 'Niobium'],
  Mo: [95.95, 'Molybdenum'],
  Tc: [96.90636, 'Technetium'],
  Ru: [101.1, 'Ruthenium'],
  Rh: [102.9055, 'Rhodium'],
  Pd: [106.42, 'Palladium'],
  Ag: [107.87, 'Silver'],
  Cd: [112.41, 'Cadmium'],
  In: [114.82, 'Indium'],
  Sn: [118.71, 'Tin'],
  Sb: [121.76, 'Antimony'],
  Te: [127.6, 'Tellurium'],
  I: [126.9, 'Iodine'],
  Xe: [131.29, 'Xenon'],
  Cs: [132.91, 'Cesium'],
  Ba: [137.33, 'Barium'],
  La: [138.9055, 'Lanthanum'],
  Ce: [140.116, 'Cerium'],
  Pr: [140.90766, 'Praseodymium'],
  Nd: [144.24, 'Neodymium'],
  Pm: [144.91276, 'Promethium'],
  Sm: [150.4, 'Samarium'],
  Eu: [151.964, 'Europium'],
  Gd: [157.25, 'Gadolinium'],
  Tb: [158.92535, 'Terbium'],
  Dy: [162.5, 'Dysprosium'],
  Ho: [164.93033, 'Holmium'],
  Er: [167.26, 'Erbium'],
  Tm: [168.93422, 'Thulium'],
  Yb: [173.05, 'Ytterbium'],
  Lu: [174.9667, 'Lutetium'],
  Hf: [178.49, 'Hafnium'],
  Ta: [180.9479, 'Tantalum'],
  W: [183.84, 'Tungsten'],
  Re: [186.207, 'Rhenium'],
  Os: [190.2, 'Osmium'],
  Ir: [192.22, 'Iridium'],
  Pt: [195.08, 'Platinum'],
  Au: [196.97, 'Gold'],
  Hg: [200.59, 'Mercury'],
  Tl: [204.38, 'Thallium'],
  Pb: [207.2, 'Lead'],
  Bi: [208.98, 'Bismuth'],
  Po: [208.98243, 'Polonium'],
  At: [209.98715, 'Astatine'],
  Rn: [222.01758, 'Radon'],
  Fr: [223.01973, 'Francium'],
  Ra: [226.02541, 'Radium'],
  Ac: [227.02775, 'Actinium'],
  Th: [232.038, 'Thorium'],
  Pa: [231.03588, 'Protactinium'],
  U: [238.03, 'Uranium'],
  Np: [237.048172, 'Neptunium'],
  Pu: [244.0642, 'Plutonium'],
  Am: [243.06138, 'Americium'],
  Cm: [247.07035, 'Curium'],
  Bk: [247.07031, 'Berkelium'],
  Cf: [251.07959, 'Californium'],
  Es: [252.083, 'Einsteinium'],
  Fm: [257.09511, 'Fermium'],
  Md: [258.09843, 'Mendelevium'],
  No: [259.101, 'Nobelium'],
  Lr: [266.12, 'Lawrencium'],
  Rf: [267.122, 'Rutherfordium'],
  Db: [268.126, 'Dubnium'],
  Sg: [269.128, 'Seaborgium'],
  Bh: [270.133, 'Bohrium'],
  Hs: [269.1336, 'Hassium'],
  Mt: [277.154, 'Meitnerium'],
  Ds: [282.166, 'Darmstadtium'],
  Rg: [282.169, 'Roentgenium'],
  Cn: [286.179, 'Copernicium'],
  Nh: [286.182, 'Nihonium'],
  Fl: [290.192, 'Flerovium'],
  Mc: [290.196, 'Moscovium'],
  Lv: [293.205, 'Livermorium'],
  Ts: [294.211, 'Tennessine'],
  Og: [295.216, 'Oganesson'],
};

export type MolarMassResult = {
  mass: number;
  parts: Array<{ symbol: string; name: string; count: number; mass: number; share: number }>;
};

function parseGroup(src: string, pos: { i: number }, closer: string | null): Map<string, number> {
  const counts = new Map<string, number>();
  const add = (sym: string, n: number) => counts.set(sym, (counts.get(sym) ?? 0) + n);
  const readNumber = () => {
    const m = /^\d+(\.\d+)?/.exec(src.slice(pos.i));
    if (!m) return 1;
    pos.i += m[0].length;
    return Number(m[0]);
  };
  while (pos.i < src.length) {
    const ch = src[pos.i];
    if (ch === '(' || ch === '[') {
      pos.i += 1;
      const inner = parseGroup(src, pos, ch === '(' ? ')' : ']');
      const k = readNumber();
      for (const [s, n] of inner) add(s, n * k);
    } else if (ch === ')' || ch === ']') {
      if (ch !== closer) throw new Error(`Unbalanced "${ch}"`);
      pos.i += 1;
      return counts;
    } else if (/[A-Z]/.test(ch)) {
      const m = /^[A-Z][a-z]?/.exec(src.slice(pos.i))!;
      let sym = m[0];
      // "Co" vs "C"+"o": prefer the two-letter symbol only if it exists.
      if (sym.length === 2 && !ATOMS[sym]) sym = sym[0];
      if (!ATOMS[sym]) throw new Error(`Unknown element "${sym}"`);
      pos.i += sym.length;
      add(sym, readNumber());
    } else if (/\s/.test(ch)) {
      pos.i += 1;
    } else {
      throw new Error(`Unexpected "${ch}"`);
    }
  }
  if (closer) throw new Error(`Missing "${closer}"`);
  return counts;
}

export function molarMass(formula: string): MolarMassResult {
  const total = new Map<string, number>();
  // Hydrates: "CuSO4·5H2O" is the salt plus 5 × H2O.
  for (const piece of formula.trim().split(/[·•*.](?=\d*[A-Z(\[])/)) {
    const m = /^(\d+(?:\.\d+)?)?(.*)$/.exec(piece.trim())!;
    const k = m[1] ? Number(m[1]) : 1;
    if (!m[2]) continue;
    for (const [s, n] of parseGroup(m[2], { i: 0 }, null))
      total.set(s, (total.get(s) ?? 0) + n * k);
  }
  if (!total.size) throw new Error('Enter a chemical formula, like H2O or C6H12O6.');
  let mass = 0;
  for (const [s, n] of total) mass += ATOMS[s][0] * n;
  const parts = [...total].map(([s, n]) => ({
    symbol: s,
    name: ATOMS[s][1],
    count: n,
    mass: ATOMS[s][0] * n,
    share: (ATOMS[s][0] * n) / mass,
  }));
  return { mass, parts };
}
